import React, { useState, useEffect } from "react";
import { Modal } from "reactstrap";
import "./NewMeeting.css";
import closeIcon from "../../assets/images/close.svg";
import { useAddNewMeetingMutation } from "./meetingSlice";
import { useGetAllEmployeesQuery } from "../auth/employeeSlice";
import { selectCurrentUser } from "../auth/authSlice";
import { useSelector } from "react-redux";
import LoadingSpinner from "../../components/common/LoadingSpinner/LoadingSpinner";
import { parse, format } from "date-fns";
import { toast } from "react-hot-toast";

const NewMeeting = (props) => {
  const [addNewMeeting, { isLoading }] = useAddNewMeetingMutation();

  const [employeeData, setEmployeedata] = useState([]);

  const {
    data: employees,
    isFetching,
    isSuccess,
    isError,
    error,
  } = useGetAllEmployeesQuery();

  useEffect(() => {
    if (employees) {
      const data = employees.ids.map((id) => {
        return employees.entities[id];
      });
      setEmployeedata(data);
    }
  }, [employees]);

  const employeeNames = employeeData.map((employee) => {
    return {
      employeeId: employee.employeeId,
      name: employee.firstName + " " + employee.lastName,
    };
  });

  const user = useSelector(selectCurrentUser);

  const [meetingId, setMeetingId] = useState(0);
  const [meetingDate, setMeetingDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [initials, setInitials] = useState(user.tag);
  const [customerName, setCustomerName] = useState("");
  const [customerStatus, setCustomerStatus] = useState("");
  const [representativeName, setRepresentativeName] = useState("");
  const [representativeId, setRepresentativeId] = useState("");
  const [matchingEmployees, setMatchingEmployees] = useState([]);

  const onMeetingDateChanged = (e) => setMeetingDate(e.target.value);
  const onInitialsChanged = (e) => setInitials(e.target.value);
  const onCustomerNameChanged = (e) => setCustomerName(e.target.value);
  const onCustomerStatusChanged = (e) => setCustomerStatus(e.target.value);

  const onRepresentativeNameChanged = (e) => {
    const value = e.target.value;
    setRepresentativeName(value);
    if (value.length > 0) {
      setRepresentativeId("");
      const matchingEmployees = employeeNames.filter((employee) =>
        employee.name.toLowerCase().startsWith(value.toLowerCase())
      );
      setMatchingEmployees(matchingEmployees);
    }
  };

  const onSuggestionSelected = (employeeId, name) => {
    setRepresentativeName(name);
    setInitials(employeeData.filter((e) => e.employeeId == employeeId)[0].tag);
    setMatchingEmployees([]);
    setRepresentativeId(employeeId);
  };

  const canSave =
    [
      meetingDate,
      initials,
      customerName,
      customerStatus,
      representativeName,
      representativeId,
      user,
    ].every(Boolean) && !isLoading;

  const onSaveMeetingClicked = async () => {
    if (canSave) {
      try {
        await addNewMeeting({
          meetingId,
          meetingDate,
          initials,
          customerName,
          customerStatus: customerStatus == "YES" ? true : false,
          representativeId,
          createById: user.userID,
        }).unwrap();

        setMeetingDate("");
        setInitials("");
        setCustomerName("");
        setCustomerStatus("");
        setRepresentativeName("");
        setRepresentativeId("");
        setMatchingEmployees([]);
        props.onClose();
        if (props.isUpdate) {
          toast.success("Meeting updated!");
        }
      } catch (err) {
        if (props.isUpdate) {
          toast.error("Failed to save the meeting");
        }
        toast.error(
          `Something unexpected happned: ${
            err?.data?.Message || err?.data?.title
          }`
        );
      }
    }
  };

  useEffect(() => {
    if (props.isUpdate) {
      const parsedDate = parse(
        props.updateRow.meetingDate,
        "d MMMM yyyy",
        new Date()
      );
      const formattedDate = format(parsedDate, "yyyy-MM-dd");
      setMeetingDate(formattedDate);
      setInitials(props.updateRow.initials);
      setCustomerName(props.updateRow.customerName);
      setCustomerStatus(props.updateRow.customerStatus ? "YES" : "NO");
      setRepresentativeName(props.updateRow.representativeName);
      setRepresentativeId(
        employeeNames.find(
          (emp) => emp.name === props.updateRow.representativeName
        )?.employeeId
      );
      setMeetingId(props.updateRow.meetingId);
    }
  }, [props.isUpdate, employeeData]);

  return (
    <>
      <Modal
        isOpen={props.show}
        toggle={props.onClose}
        className="modal-dialog-centered rounded"
      >
        <div className="bg-nav p-2 rounded">
          {isLoading && <LoadingSpinner />}
          <div className="header-gradient p-2 child rounded d-flex justify-content-between mb-2">
            <h6 className="modal-title" id="title">
              {props.isUpdate ? "Update Meeting" : "Add New Meeting"}
            </h6>
            <button
              type="button"
              className="closeBtn"
              aria-label="Close"
              onClick={props.onClose}
            >
              <img src={closeIcon} style={{ width: "70px" }} alt="" />
            </button>
          </div>
          <div className="bg-white p-2">
            <form className="p-2">
              <div className="row">
                <div className="form-group col-sm-12 col-md-6">
                  <label htmlFor="date" className="text-muted">
                    Date
                  </label>
                  <input
                    type="date"
                    className="form-control custom-input"
                    id="date"
                    value={meetingDate}
                    onChange={onMeetingDateChanged}
                  />
                </div>
                <div className="form-group col-sm-12 col-md-6">
                  <label htmlFor="Initials" className="text-muted">
                    Initials
                  </label>
                  <select
                    className="form-control custom-input"
                    id="Initials"
                    value={initials}
                    onChange={onInitialsChanged}
                  >
                    {employeeData.map((employee, index) => (
                      <option key={index} value={employee.tag}>
                        {employee.tag}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="customer" className="text-muted">
                  Customer
                </label>
                <input
                  type="text"
                  className="form-control custom-input"
                  id="customer"
                  value={customerName}
                  onChange={onCustomerNameChanged}
                />
              </div>
              <div className="mb-3">
                <span className="text-muted me-3"> New Customer? </span>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="newClient"
                    id="yesRadio"
                    value="YES"
                    checked={customerStatus === "YES"}
                    onChange={onCustomerStatusChanged}
                  />
                  <label
                    className="form-check-label checkbox-label"
                    htmlFor="yesRadio"
                  >
                    Yes
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="newClient"
                    id="noRadio"
                    value="NO"
                    checked={customerStatus === "NO"}
                    onChange={onCustomerStatusChanged}
                  />
                  <label
                    className="form-check-label checkbox-label"
                    htmlFor="noRadio"
                  >
                    No
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="representative" className="text-muted">
                  Representative
                </label>
                <input
                  type="text"
                  className="form-control custom-input"
                  id="representative"
                  value={representativeName}
                  onChange={onRepresentativeNameChanged}
                />
                {matchingEmployees.length > 0 && (
                  <ul className="mb-3 suggestion-list">
                    {matchingEmployees.map((employee) => (
                      <li
                        key={employee.employeeId}
                        onClick={() =>
                          onSuggestionSelected(
                            employee.employeeId,
                            employee.name
                          )
                        }
                      >
                        {employee.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                className="btn btn-gradient w-100"
                onClick={onSaveMeetingClicked}
                disabled={!canSave}
              >
                {props.isUpdate ? "Update Meeting" : " Add Meeting"}
              </button>
            </form>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default NewMeeting;
